import { inject, Service } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiResponse, ENDPOINTS, SearchEnum } from '../../../../common';
import {
  Delegation,
  DelegationDetails,
  DelegationFormPayload,
} from './delegation.model';
import { SortColumn } from '../../../../common/components/filtered-table/filtered-table.model';
import { HttpClient, HttpParams } from '@angular/common/http';

@Service()
export class DelegationService {
  private readonly http = inject(HttpClient);
  private readonly delegationIdPlaceholder = '{id}' as const;

  private addFiltersToParams(
    params: HttpParams,
    filters: Record<string, string | string[]>
  ): HttpParams {
    Object.entries(filters).forEach(([key, value]) => {
      if (key !== SearchEnum.keyword) {
        params = params.append(`filters[${key}]`, value as string);
      }
    });
    return params;
  }

  getDelegations(
    page: number,
    size: number,
    sortColumn: SortColumn,
    filters: Record<string, string | string[]>,
    search: string
  ): Observable<ApiResponse<Delegation>> {
    return this.fetchDelegations(
      ENDPOINTS.AccountSettings.Delegations,
      page,
      size,
      sortColumn,
      filters,
      search
    );
  }

  getReceivedDelegations(
    page: number,
    size: number,
    sortColumn: SortColumn,
    filters: Record<string, string | string[]>,
    search: string
  ): Observable<ApiResponse<Delegation>> {
    return this.fetchDelegations(
      ENDPOINTS.AccountSettings.ReceivedDelegations,
      page,
      size,
      sortColumn,
      filters,
      search
    );
  }

  private fetchDelegations(
    url: string,
    page: number,
    size: number,
    sortColumn: SortColumn,
    filters: Record<string, string | string[]>,
    search: string
  ): Observable<ApiResponse<Delegation>> {
    let params = new HttpParams().append('page', page).append('per_page', size);

    if (sortColumn.key) {
      params = params.append(`sort[${sortColumn.key}]`, sortColumn.value);
    }

    if (search) {
      params = params.append(SearchEnum.keyword, search);
    }

    if (filters) {
      params = this.addFiltersToParams(params, filters);
    }

    return this.http.get<ApiResponse<Delegation>>(url, { params });
  }

  getDelegation(id: string): Observable<{ data: DelegationDetails }> {
    return this.http.get<{ data: DelegationDetails }>(
      ENDPOINTS.AccountSettings.DelegationById.replace(
        this.delegationIdPlaceholder,
        id
      )
    );
  }

  createDelegation(
    payload: DelegationFormPayload
  ): Observable<{ data: DelegationDetails }> {
    return this.http.post<{ data: DelegationDetails }>(
      ENDPOINTS.AccountSettings.AddDelegation,
      payload
    );
  }

  updateDelegation(
    id: string,
    payload: DelegationFormPayload
  ): Observable<{ data: DelegationDetails }> {
    return this.http.put<{ data: DelegationDetails }>(
      ENDPOINTS.AccountSettings.EditDelegation.replace(
        this.delegationIdPlaceholder,
        id
      ),
      payload
    );
  }

  cancelDelegation(id: string): Observable<{ data: DelegationDetails }> {
    return this.http.post<{ data: DelegationDetails }>(
      ENDPOINTS.AccountSettings.CancelDelegation.replace(
        this.delegationIdPlaceholder,
        id
      ),
      {}
    );
  }

  revokeDelegation(id: string): Observable<{ data: DelegationDetails }> {
    return this.http.post<{ data: DelegationDetails }>(
      ENDPOINTS.AccountSettings.RevokeDelegation.replace(
        this.delegationIdPlaceholder,
        id
      ),
      {}
    );
  }
}
